import memoize from '@github/memoize'
import {getEnv} from '@github-ui/client-env'
import {IS_SERVER} from '@github-ui/ssr-utils'
import type {JSFeatureFlag} from './client-feature-flags'

export type FeatureFlagEvaluationListener = (name: JSFeatureFlag, enabled: boolean) => void

const evaluatedFeatures = IS_SERVER ? undefined : new Map<JSFeatureFlag, boolean>()
const evaluationListeners = IS_SERVER ? undefined : new Set<FeatureFlagEvaluationListener>()

function getEnabledFeaturesSet() {
  return new Set(getEnv().featureFlags as JSFeatureFlag[])
}

const featuresSet =
  IS_SERVER || process.env.NODE_ENV === 'test' || isStorybook() ? getEnabledFeaturesSet : memoize(getEnabledFeaturesSet)

export function getEnabledFeatures(): JSFeatureFlag[] {
  return Array.from(featuresSet())
}

/**
 * This function accesses feature flag states which have been loaded into the client env via the `client-feature-flags.ts` file.
 * See https://thehub.github.com/epd/engineering/products-and-services/dotcom/features/feature-flags/typescript-frontend
 * for more details.
 *
 * If you're trying to check a feature flag that was added to the `enabled_features` of a React payload via `add_client_feature_flag` in a
 * Rails controller, you should use the `useFeatureFlag` hook (from `@github-ui/react-core/use-feature-flag`) instead to access the feature flag state.
 * https://thehub.github.com/epd/engineering/products-and-services/dotcom/features/feature-flags/typescript-frontend has
 * more information on `useFeatureFlag` and `add_client_feature_flag`.
 */
export function isFeatureEnabled(name: JSFeatureFlag): boolean {
  const enabled = featuresSet().has(name)

  if (evaluatedFeatures && evaluationListeners && evaluatedFeatures.get(name) !== enabled) {
    evaluatedFeatures.set(name, enabled)
    for (const listener of evaluationListeners) listener(name, enabled)
  }

  return enabled
}

export function isFeatureEnabledWithOptOut(name: JSFeatureFlag, optOutName: JSFeatureFlag): boolean {
  return isFeatureEnabled(name) && !isFeatureEnabled(optOutName)
}

export function subscribeToFeatureFlagEvaluations(listener: FeatureFlagEvaluationListener): () => void {
  if (!evaluatedFeatures || !evaluationListeners) return () => undefined

  for (const [name, enabled] of evaluatedFeatures) listener(name, enabled)
  evaluationListeners.add(listener)
  return () => evaluationListeners.delete(listener)
}

export function resetFeatureFlagEvaluationsForTesting(): void {
  evaluatedFeatures?.clear()
  evaluationListeners?.clear()
}

// exported to allow mocking in tests
const featureFlag = {isFeatureEnabled}

export {featureFlag}

function isStorybook() {
  try {
    return process?.env?.STORYBOOK === 'true' || process?.env?.APP_ENV === 'storybook'
  } catch {
    return false
  }
}
